import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================================================
// Enums for Rule Category
// ============================================================================

export const RuleCategoryEnum = z.enum([
  "conditional",
  "non_conditional",
  "redistribution",
]);

export type RuleCategory = z.infer<typeof RuleCategoryEnum>;

// ============================================================================
// Validation Issue Types
// ============================================================================

export const ReportedImportIssueTypeEnum = z.enum([
  "unmatched_supplier",
  "unmatched_division",
  "no_agreement_found",
  "no_estimates_for_proportioning",
  "invalid_date",
  "invalid_amount",
]);

export type ReportedImportIssueType = z.infer<
  typeof ReportedImportIssueTypeEnum
>;

export const ReportedImportIssueSeverityEnum = z.enum(["error", "warning"]);

export type ReportedImportIssueSeverity = z.infer<
  typeof ReportedImportIssueSeverityEnum
>;

// ============================================================================
// Parsed/Aggregated Data Schema
// ============================================================================

export const AggregatedReportedRowSchema = z.object({
  // Aggregation key components
  supplierReference: z.string(), // rexel_code from xlsx
  supplierName: z.string(), // Original supplier name from xlsx
  divisionReference: z.string(), // Division reference code from xlsx
  divisionName: z.string(), // Division name from xlsx
  month: z.string(), // YYYY-MM format
  category: RuleCategoryEnum, // Derived from Program line type

  // Aggregated value
  accrualEarnings: z.number(),

  // Match suggestions
  suggestedSupplierId: z.string().uuid().nullable(),
  suggestedSupplierName: z.string().nullable(),
  suggestedDivisionId: z.string().uuid().nullable(),
  suggestedDivisionName: z.string().nullable(),
  suggestedAgreementId: z.string().uuid().nullable(),
  suggestedAgreementName: z.string().nullable(),

  // Row count (how many xlsx rows were aggregated)
  rowCount: z.number(),
});

export type AggregatedReportedRow = z.infer<typeof AggregatedReportedRowSchema>;

// ============================================================================
// Supplier/Division Match Suggestion Schema
// ============================================================================

export const SupplierSuggestionSchema = z.object({
  supplierReference: z.string(),
  supplierName: z.string(),
  suggestedSupplierId: z.string().uuid().nullable(),
  suggestedSupplierName: z.string().nullable(),
  suggestedAgreementId: z.string().uuid().nullable(),
  suggestedAgreementName: z.string().nullable(),
  totalAmount: z.number(),
  rowCount: z.number(),
});

export type SupplierSuggestion = z.infer<typeof SupplierSuggestionSchema>;

export const DivisionSuggestionSchema = z.object({
  divisionReference: z.string(),
  divisionName: z.string(),
  suggestedDivisionId: z.string().uuid().nullable(),
  suggestedDivisionName: z.string().nullable(),
  totalAmount: z.number(),
  rowCount: z.number(),
});

export type DivisionSuggestion = z.infer<typeof DivisionSuggestionSchema>;

// ============================================================================
// Mapping Schema (user-confirmed)
// ============================================================================

export const ReportedSupplierMappingSchema = z.object({
  supplierReference: z.string(),
  supplierId: z.string().uuid(),
  agreementId: z.string().uuid(),
});

export type ReportedSupplierMapping = z.infer<
  typeof ReportedSupplierMappingSchema
>;

export const ReportedDivisionMappingSchema = z.object({
  divisionReference: z.string(),
  divisionId: z.string().uuid(),
});

export type ReportedDivisionMapping = z.infer<
  typeof ReportedDivisionMappingSchema
>;

// ============================================================================
// Validation Issue Schema
// ============================================================================

export const ReportedValidationIssueSchema = z.object({
  type: ReportedImportIssueTypeEnum,
  severity: ReportedImportIssueSeverityEnum,
  message: z.string(),
  // Additional context
  supplierReference: z.string().nullable(),
  divisionReference: z.string().nullable(),
  month: z.string().nullable(),
  amount: z.number().nullable(),
});

export type ReportedValidationIssue = z.infer<
  typeof ReportedValidationIssueSchema
>;

// ============================================================================
// Step 1: Parse Reported File
// ============================================================================

export const ParseReportedFileRequestSchema = z.object({
  xlsxBase64: z.string(),
  // Target month in YYYY-MM format (e.g., "2025-12")
  // This is required as the file represents a single month's data
  targetMonth: z.string().regex(/^\d{4}-\d{2}$/, "Must be YYYY-MM format"),
});

export const ParseReportedFileResponseSchema = z.object({
  // Aggregated rows for display
  aggregatedRows: z.array(AggregatedReportedRowSchema),

  // Unique suppliers with suggestions
  supplierSuggestions: z.array(SupplierSuggestionSchema),

  // Unique divisions with suggestions
  divisionSuggestions: z.array(DivisionSuggestionSchema),

  // Stats
  totalRows: z.number(),
  aggregatedRowCount: z.number(),
  totalAmount: z.number(),
  uniqueSuppliers: z.number(),
  uniqueDivisions: z.number(),

  // Issues found during parsing
  validationIssues: z.array(ReportedValidationIssueSchema),
});

export type ParseReportedFileRequest = z.infer<
  typeof ParseReportedFileRequestSchema
>;
export type ParseReportedFileResponse = z.infer<
  typeof ParseReportedFileResponseSchema
>;

// ============================================================================
// Step 2: Import Reported Data
// ============================================================================

export const ImportReportedDataRequestSchema = z.object({
  xlsxBase64: z.string(),
  // Target month in YYYY-MM format (e.g., "2025-12")
  targetMonth: z.string().regex(/^\d{4}-\d{2}$/, "Must be YYYY-MM format"),
  // User-confirmed supplier mappings (supplierReference -> supplierId + agreementId)
  supplierMappings: z.array(ReportedSupplierMappingSchema),
  // User-confirmed division mappings (divisionReference -> divisionId)
  divisionMappings: z.array(ReportedDivisionMappingSchema),
});

export const BreakdownImportResultSchema = z.object({
  supplierReference: z.string(),
  supplierName: z.string(),
  agreementId: z.string().uuid(),
  agreementName: z.string(),
  breakdownsCreated: z.number(),
  totalAmount: z.number(),
});

export type BreakdownImportResult = z.infer<typeof BreakdownImportResultSchema>;

/**
 * Agreement that had existing reported data at the import date but was NOT
 * in the imported file. Its cumulative was wiped to $0 (since the file is
 * treated as the source of truth) and a rollback breakdown was inserted if
 * it carried a prior-month balance.
 */
export const MissingAgreementSchema = z.object({
  agreementId: z.string().uuid(),
  agreementName: z.string(),
  supplierName: z.string(),
  /** Cumulative amount that was wiped (the pre-import value at the target month). */
  existingCumulativeAmount: z.number(),
});

export type MissingAgreement = z.infer<typeof MissingAgreementSchema>;

export const ImportReportedDataResponseSchema = z.object({
  success: z.boolean(),
  // Totals
  breakdownsCreated: z.number(),
  breakdownsDeleted: z.number(),
  totalAmount: z.number(),
  // Skipped
  skippedRows: z.number(),
  // Breakdown by supplier
  bySupplier: z.array(BreakdownImportResultSchema),
  // Agreements that had reported data at the import date but were NOT in the
  // file — their cumulative was wiped (and zero-rolled-back where applicable).
  missingAgreements: z.array(MissingAgreementSchema),
  // Warnings that didn't block import
  warnings: z.array(z.string()),
  // Validation issues
  validationIssues: z.array(ReportedValidationIssueSchema),
});

export type ImportReportedDataRequest = z.infer<
  typeof ImportReportedDataRequestSchema
>;
export type ImportReportedDataResponse = z.infer<
  typeof ImportReportedDataResponseSchema
>;

// ============================================================================
// Get All Divisions (for dropdown)
// ============================================================================

export const GetDivisionsForMappingResponseSchema = z.object({
  divisions: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      internalCode: z.string().nullable(),
    }),
  ),
});

export type GetDivisionsForMappingResponse = z.infer<
  typeof GetDivisionsForMappingResponseSchema
>;

// ============================================================================
// Contract Definitions
// ============================================================================

export const historicalReportedImportContracts = {
  parseReportedFile: oc
    .input(ParseReportedFileRequestSchema)
    .output(ParseReportedFileResponseSchema)
    .route({ method: "POST" }),

  importReportedData: oc
    .input(ImportReportedDataRequestSchema)
    .output(ImportReportedDataResponseSchema)
    .route({ method: "POST" }),

  getDivisionsForMapping: oc
    .output(GetDivisionsForMappingResponseSchema)
    .route({ method: "GET" }),
} as const;
